/**
 * Die Fehler- und 404-Seite von Next.js — mit Kopf, `<main>` und dem
 * Legal-Footer (Regel #21: „auch 404 und Fehlerseiten").
 *
 * Bis zum 03.10.2026 endete eine Server-Seite, die das Backend nicht erreicht
 * (Startseite, Landingpages), und ein Bündel der App, das nicht lädt, in der
 * Standardseite von Next — „This page couldn't load", ohne Impressum und
 * Datenschutz.
 *
 * Ohne Hook und ohne `'use client'`: Die 404 entsteht auf dem Server, die
 * Fehlerseiten im Browser — beide rendern dieses Markup. Links sind schlichte
 * `<a href>`, denn hier läuft kein Router; sie tragen das Sprach-Präfix der
 * Adresse, wenn es eines gibt, sonst leitet die App die flache Adresse selbst
 * in die Sprachfassung weiter.
 *
 * `noindex`: Eine Fehlerseite ist kein Inhalt.
 */
import { footerZiel } from '../../utils/legal';
import LegalFooterInhalt, { type FooterUebersetzer } from '../LegalFooterInhalt';
import StandardLogo from '../StandardLogo';
import HinweisLeisteFehlerseite, { type FehlerseitenHinweis } from './HinweisLeisteFehlerseite';

interface Eigenschaften {
  t: FooterUebersetzer;
  art: 'fehler' | 'nicht_gefunden';
  /** `/de/deutsch` oder `''` — vor jedes interne Ziel gesetzt. */
  praefix: string;
  /** Nur bei `fehler`: Ein Bündel, das nach einem Deploy nicht mehr existiert,
   *  lädt kein `reset()` nach — erst ein neuer Seitenaufruf holt die neuen. */
  neuLaden?: () => void;
  /** Nur bei `nicht_gefunden` (auf dem Server geholt): Hinweise, die niemand
   *  wegklicken kann — der Beta-Hinweis steht auf jeder Seite, auch hier. */
  hinweise?: readonly FehlerseitenHinweis[];
  /** Statt `neuLaden`, wo kein JavaScript läuft (Server-Seite ohne Backend):
   *  dieselbe Adresse als Link — ein neuer Seitenaufruf ist das Neu-Laden. */
  neuLadenAdresse?: string;
}

export default function FehlerSeite({ t, art, praefix, neuLaden, neuLadenAdresse, hinweise = [] }: Eigenschaften) {
  const titel = art === 'fehler'
    ? t('fehlerseite.titel', 'Diese Seite konnte nicht geladen werden')
    : t('not_found.title', 'Seite nicht gefunden');
  const start = praefix || '/';

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <title>{titel}</title>
      <meta name="robots" content="noindex" />
      <HinweisLeisteFehlerseite
        hinweise={hinweise}
        mehr={t('banner.more', 'Mehr')}
        weniger={t('banner.less', 'Weniger')}
      />
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center px-4 py-3 sm:px-6">
          <a href={start} className="inline-flex items-center">
            <StandardLogo alt="Paperball" className="block h-8 w-auto max-w-full sm:h-14" />
          </a>
        </div>
      </header>

      <main className="flex-1">
        <div className="mx-auto w-full max-w-2xl space-y-4 px-4 py-16 text-center sm:px-6">
          <h1 className="text-2xl font-semibold text-slate-900">{titel}</h1>
          <p className="text-slate-600">
            {art === 'fehler'
              ? t('fehlerseite.text', 'Beim Laden ist ein Fehler aufgetreten. Lade die Seite neu — meist ist das Problem danach behoben. Hält es an, schreib uns.')
              : t('fehlerseite.nicht_gefunden_text', 'Diese Adresse gibt es hier nicht. Vielleicht ist der Link veraltet oder vertippt.')}
          </p>
          <div className="flex flex-wrap justify-center gap-3 pt-2">
            {art === 'fehler' && neuLaden && (
              <button type="button" onClick={neuLaden} className="btn btn-primary">
                {t('error_boundary.reload_page', 'Seite neu laden')}
              </button>
            )}
            {art === 'fehler' && !neuLaden && neuLadenAdresse && (
              <a href={neuLadenAdresse} className="btn btn-primary">
                {t('error_boundary.reload_page', 'Seite neu laden')}
              </a>
            )}
            <a href={start} className={art === 'fehler' ? 'btn btn-secondary' : 'btn btn-primary'}>
              {t('not_found.home', 'Zur Startseite')}
            </a>
          </div>
        </div>
      </main>

      <LegalFooterInhalt
        t={t}
        intern={(l, text) => <a href={footerZiel(l, (pfad) => `${praefix}${pfad}`)} className="hover:text-gray-700">{text}</a>}
      />
    </div>
  );
}
